import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  onAuthStateChanged,
  signOut,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from 'firebase/auth';
import { auth } from '../config/firebaseClient';
import { fetchProfileByUid, upsertProfile } from '../services/profileService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginRedirect, setLoginRedirect] = useState(null);
  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  const loadProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setProfile(null);
      return null;
    }
    try {
      const data = await fetchProfileByUid(firebaseUser.uid);
      setProfile(data);
      return data;
    } catch (err) {
      console.error('Failed to load profile:', err);
      setProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      await loadProfile(firebaseUser);
      setAuthLoading(false);
    });
    return unsubscribe;
  }, [loadProfile]);

  const clearRecaptcha = useCallback(() => {
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.clear();
      } catch (_) {
        /* already cleared */
      }
      recaptchaRef.current = null;
    }
  }, []);

  const setupRecaptcha = useCallback(() => {
    clearRecaptcha();
    recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
    });
    return recaptchaRef.current;
  }, [clearRecaptcha]);

  const sendOtp = useCallback(async (phoneE164) => {
    const verifier = setupRecaptcha();
    const confirmation = await signInWithPhoneNumber(auth, phoneE164, verifier);
    confirmationRef.current = confirmation;
    return confirmation;
  }, [setupRecaptcha]);

  const verifyOtp = useCallback(async (code) => {
    if (!confirmationRef.current) {
      throw new Error('No OTP session. Please request a new code.');
    }
    const result = await confirmationRef.current.confirm(code);
    const firebaseUser = result.user;
    const phone = firebaseUser.phoneNumber;

    const saved = await upsertProfile({
      firebase_uid: firebaseUser.uid,
      phone,
    });
    setProfile(saved);
    confirmationRef.current = null;
    clearRecaptcha();
    return firebaseUser;
  }, [clearRecaptcha]);

  const logout = useCallback(async () => {
    await signOut(auth);
    setProfile(null);
    confirmationRef.current = null;
    clearRecaptcha();
  }, [clearRecaptcha]);

  const refreshProfile = useCallback(async () => {
    if (!user) return null;
    return loadProfile(user);
  }, [user, loadProfile]);

  const saveProfileDetails = useCallback(async (fields) => {
    if (!user) throw new Error('Not signed in');
    const saved = await upsertProfile({
      firebase_uid: user.uid,
      phone: user.phoneNumber || profile?.phone,
      ...fields,
    });
    setProfile(saved);
    return saved;
  }, [user, profile?.phone]);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        authLoading,
        loginModalOpen,
        setLoginModalOpen,
        loginRedirect,
        setLoginRedirect,
        sendOtp,
        verifyOtp,
        logout,
        refreshProfile,
        saveProfileDetails,
        clearRecaptcha,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
